import * as React from 'react';
import { useEffect, useState } from 'react';
// @ts-ignore
import { default as Form } from 'components/connectedForm';
import BlurTextField from '../connectedForm/BlurTextField';
import { BlurTextFieldType } from '../connectedForm/BlurTextField/BlurTextField';
import Dropdown from '../connectedForm/Dropdown';
import DateInput from '../connectedForm/DateInput/DateInput';
import GridUploader from '../connectedForm/GridUploader';
import {
  FixedHeaderFooterSection,
  FixedHeaderFooterSectionPropsType,
  TEXT_AREA_STYLES,
} from 'components/modals/FixedHeaderFooterSection';
import getValue from '../connectedForm/utils/getValue';
import { DisplayListSimple } from '../connectedForm/inputComponents';
import { useMutation } from '@apollo/client';
import SubmitSupportMutation from '../../graphql/mutations/company/SubmitSupportMutation.graphql';
import getClientMutationId from '../../utils/getClientMutationId';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { useFullUserDetails } from '../../utils/hooks';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import {
  AlertType,
  ISubmitSupportRequestMutationResult,
  ISubmitSupportRequestMutationVariables,
} from '../../graphql/schema/p4types';

const MODULES = [
  { label: 'Company', value: 'Company' },
  { label: 'Customers', value: 'Customers' },
  { label: 'Suppliers', value: 'Suppliers' },
  { label: 'Products', value: 'Products' },
  { label: 'Sales', value: 'Sales' },
  { label: 'Diary', value: 'Diary' },
  { label: 'Purchasing', value: 'Purchasing' },
  { label: 'Nominal', value: 'Nominal' },
  { label: 'Vat', value: 'Vat' },
  { label: 'Bank', value: 'Bank' },
  { label: 'Web', value: 'Web' },
  { label: 'Reports', value: 'Reports' },
  { label: 'System', value: 'System' },
];

const ISSUE_TYPES = [
  { label: 'How-to/Guidance', value: 'How-to/Guidance' },
  { label: 'Error', value: 'Error' },
];

const SEVERITY_LEVELS = [
  { label: 'Critical [System Down]', value: 'Critical [System Down]' },
  { label: 'High [Business Impact]', value: 'High [Business Impact]' },
  { label: 'Medium', value: 'Medium [Workaround Available]' },
  { label: 'Low', value: 'Low' },
];

export const SubmitRequestForm = ({ onClose }: FixedHeaderFooterSectionPropsType) => {
  const fullUserDetails = useFullUserDetails();
  const { dispatchSimpleToast } = useP4ToastController();

  const [customerName, setCustomerName] = useState<string>();
  const [contactName, setContactName] = useState<string>();
  const [contactEmail, setContactEmail] = useState<string>();
  const [contactPhoneNumber, setContactPhoneNumber] = useState<string>();
  const [companyName, setCompanyName] = useState<string>();

  const [moduleAffected, setModuleAffected] = useState<string>();
  const [issueType, setIssueType] = useState<string>();
  const [severity, setSeverity] = useState<string>('Low');

  const [summary, setSummary] = useState<string>();
  const [detailedDescription, setDetailedDescription] = useState<string>();
  const [stepsToReproduce, setStepsToReproduce] = useState<string[]>();

  const [userRole, setUserRole] = useState<string>();
  const toLocalISOString = (date: Date) => {
    const offsetMs = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
  };

  const [dateOfIssue, setDateOfIssue] = useState<string>(toLocalISOString(new Date()));
  const [attachments, setAttachments] = useState<any[]>();

  const handleQueryError = useComponentQueryErrorHandling();

  useEffect(() => {
    setContactName(fullUserDetails?.systemUser?.name ?? '');
    setContactEmail(fullUserDetails?.systemUser?.email ?? '');
    setCompanyName(fullUserDetails?.currentCompany?.name ?? '');

    if (fullUserDetails?.systemUser?.admin) {
      setUserRole('Admin');
    }
  }, [fullUserDetails]);

  const [submitSupportMutation] = useMutation<
    ISubmitSupportRequestMutationResult,
    ISubmitSupportRequestMutationVariables
  >(SubmitSupportMutation, {
    onError: handleQueryError,
  });

  const handleSubmitForm = () => {
    submitSupportMutation({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          supportRequest: {
            customerName: customerName,
            contactName: contactName,
            contactEmail: contactEmail,
            contactPhoneNumber: contactPhoneNumber,
            companyName: companyName,
            moduleAffected: moduleAffected,
            issueType: issueType,
            severity: severity,
            summary: summary,
            detailedDescription: detailedDescription,
            stepsToReproduce: stepsToReproduce,
            attachments: attachments,
            userRole: userRole,
            dateOfIssue: dateOfIssue,
          },
        },
      },
    }).then((response) => {
      if (!response.errors) {
        dispatchSimpleToast(
          'Support Request Submitted',
          'Support request has successfully been submitted.',
          AlertType.Success
        );
        onClose();
      }
    });
  };

  return (
    <FixedHeaderFooterSection title="Submit Request" onClose={onClose} onConfirm={handleSubmitForm}>
      <Form.Fieldset cols={1}>
        <Form.Legend label="Customer Information" />
        <BlurTextField
          value={customerName}
          label={'Customer Name'}
          labelCols={1 / 3}
          inputCols={2 / 3}
          data-testid={'submit-request-customer-name'}
          mandatory={true}
          enableOnChange={true}
          onChange={setCustomerName}
        />
        <BlurTextField
          value={contactName}
          label={'Contact Name'}
          labelCols={1 / 3}
          inputCols={2 / 3}
          data-testid={'submit-request-contact-name'}
          mandatory={true}
          enableOnChange={true}
          onChange={setContactName}
        />
        <BlurTextField
          value={contactEmail}
          label={'Contact Email'}
          labelCols={1 / 3}
          inputCols={2 / 3}
          data-testid={'submit-request-contact-email'}
          mandatory={true}
          enableOnChange={true}
          onChange={setContactEmail}
        />
        <BlurTextField
          value={contactPhoneNumber}
          label={'Contact Phone Number'}
          labelCols={1 / 3}
          inputCols={2 / 3}
          data-testid={'submit-request-contact-phone-number'}
          mandatory={true}
          enableOnChange={true}
          onChange={setContactPhoneNumber}
        />
        <BlurTextField
          value={companyName}
          label={'Company Name'}
          labelCols={1 / 3}
          inputCols={2 / 3}
          data-testid={'submit-request-company-name'}
          mandatory={true}
          enableOnChange={true}
          onChange={setCompanyName}
        />
      </Form.Fieldset>

      <Form.Fieldset cols={1}>
        <Form.Legend label="Issue Categorisation" />
        <Dropdown
          label="Product/Module Affected"
          value={moduleAffected}
          onChange={(value: { value: string }) => {
            setModuleAffected(value.value);
          }}
          options={MODULES}
          labelCols={1 / 3}
          inputCols={2 / 3}
          data-testid={'submit-request-module-affected'}
          mandatory={true}
        />
        <Dropdown
          label="Issue Type"
          value={issueType}
          onChange={(value: { value: string }) => {
            setIssueType(value.value);
          }}
          options={ISSUE_TYPES}
          labelCols={1 / 3}
          inputCols={2 / 3}
          data-testid={'submit-request-issue-type'}
          mandatory={true}
        />
        <Dropdown
          label="Severity/Urgency"
          value={severity}
          onChange={(value: { value: string }) => {
            setSeverity(value.value);
          }}
          options={SEVERITY_LEVELS}
          labelCols={1 / 3}
          inputCols={2 / 3}
          data-testid={'submit-request-severity'}
          mandatory={true}
        />
      </Form.Fieldset>

      <Form.Fieldset cols={1}>
        <Form.Legend label="Issue Description" />
        <BlurTextField
          value={summary}
          label={'Summary'}
          labelCols={1 / 3}
          inputCols={2 / 3}
          data-testid={'submit-request-summary'}
          placeholder={'Enter a brief summary of the issue'}
          mandatory={true}
          enableOnChange={true}
          onChange={setSummary}
        />
        <BlurTextField
          data-testid={'submit-request-detailed-description'}
          value={detailedDescription}
          labelCols={1 / 3}
          inputCols={2 / 3}
          label={'Detailed Description'}
          placeholder={
            'Provide detailed context or observations. You can also include a video link or links to any relevant resources.'
          }
          onBlur={(value) => setDetailedDescription(getValue(value))}
          type={BlurTextFieldType.ExpandableTextArea}
          style={TEXT_AREA_STYLES}
          mandatory={true}
        />
      </Form.Fieldset>

      <Form.Fieldset cols={1}>
        <Form.Legend label="Steps to Reproduce" />
        <DisplayListSimple
          data-testid={'submit-request-steps-to-reproduce'}
          value={stepsToReproduce}
          onChange={setStepsToReproduce}
        />
      </Form.Fieldset>

      <Form.Fieldset cols={1}>
        <Form.Legend label="Attachments & Supporting Information" />
        <GridUploader value={attachments} onChange={setAttachments} />
      </Form.Fieldset>

      <Form.Fieldset cols={1}>
        <Form.Legend label="Environment Details" />
        <BlurTextField
          value={userRole}
          label={'User Role/Access Level'}
          labelCols={1 / 3}
          inputCols={2 / 3}
          data-testid={'submit-request-user-role'}
          mandatory={true}
          enableOnChange={true}
          onChange={setUserRole}
        />
        <DateInput
          data-testid="submit-request-date-of-issue"
          label="Date/Time of Issue"
          mandatory={true}
          portal={true}
          value={dateOfIssue}
          onChange={setDateOfIssue}
          labelCols={1 / 3}
          inputCols={2 / 3}
          showTimeSelect={true}
        />
      </Form.Fieldset>
    </FixedHeaderFooterSection>
  );
};
